// The lasting table. A lasting effect is a spell that changes a count of a
// side or a rule for some turns, and no piece: no capture, no energy, a
// cheaper roll. A piece has its badges for a trait. A lasting effect has none,
// so the team sheet shows a seal for each side it binds, and the board frame
// shows a band. Both iterate LASTING and never name a field.
// Added 2026-10-14. Garrett asked how Demotivation and Chill Pill should show,
// and chose bands in the frame from a page of three options.
// The views do not read the state. They read the counts, which
// `state/lastingShown.ts` holds back until the beat of the change.

import { FORCED_PEACE_PLIES, LOST_ENERGY_TURNS, ROLL_DISCOUNT_ROLLS } from '../constants';
import type { Side } from '../engine/types';
import type { SpellId, SpellPolarity } from '../spells/types';
import type { GameState } from './state';

const SIDES: readonly Side[] = ['gold', 'violet'];

export type LastingKey = 'noCapturePlies' | 'energyHold' | 'rollDiscounts';

/** For each effect, the turns or rolls left that bind each side. Zero is none. */
export type LastingCounts = Readonly<Record<LastingKey, Readonly<Record<Side, number>>>>;

export type Lasting = {
  key: LastingKey;
  /** The spell that sets it. The seal shows its icon, and the band and the seal take its polarity. */
  spellId: SpellId;
  polarity: Exclude<SpellPolarity, 'any'>;
  /** The count a cast gives, so the ring of the seal has a segment for each. */
  full: number;
  /** One plain line for the tooltip of the seal. */
  describe: (left: number) => string;
};

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

export const LASTING: readonly Lasting[] = [
  {
    key: 'noCapturePlies',
    spellId: 'forced-peace',
    polarity: 'board',
    full: FORCED_PEACE_PLIES,
    describe: (left) => `No piece can capture. ${plural(left, 'turn')} left.`,
  },
  {
    key: 'energyHold',
    spellId: 'lost-energy',
    polarity: 'enemy',
    full: LOST_ENERGY_TURNS,
    describe: (left) => `No energy at the start of the turn. ${plural(left, 'turn')} left.`,
  },
  {
    key: 'rollDiscounts',
    spellId: 'roll-discount',
    polarity: 'ally',
    full: ROLL_DISCOUNT_ROLLS,
    describe: (left) => `A roll costs less. ${plural(left, 'roll')} left.`,
  },
];

/** The counts of a state. A rule of the board binds both sides. It counts plies, one for each turn of either side. */
export const lastingCountsOf = (state: GameState): LastingCounts => ({
  noCapturePlies: { gold: state.rules.noCapturePlies, violet: state.rules.noCapturePlies },
  energyHold: state.energyHold,
  rollDiscounts: state.rollDiscounts,
});

export type LastingOn = {
  lasting: Lasting;
  left: number;
  /** The segments of the ring. A second cast adds to the count, so it can be more than `full`. */
  total: number;
};

/** The lasting effects that bind `side`, in the order of the table. */
export const lastingOn = (counts: LastingCounts, side: Side): LastingOn[] =>
  LASTING.flatMap((lasting) => {
    const left = counts[lasting.key][side];
    return left > 0 ? [{ lasting, left, total: Math.max(lasting.full, left) }] : [];
  });

/** The sides `lasting` binds. */
export const sidesBound = (counts: LastingCounts, lasting: Lasting): Side[] =>
  SIDES.filter((side) => counts[lasting.key][side] > 0);

/** A change of this string is a change of what the seals and the bands show. */
export const lastingSignature = (counts: LastingCounts): string =>
  SIDES.map((side) =>
    lastingOn(counts, side)
      .map(({ lasting, left }) => `${lasting.key}${left}`)
      .join(','),
  ).join('|');
